<template>
    <b-col class="p-0">
        <div class="nav-indicators bg-white border-bottom w-100">
            <button
                v-if="previousLesson"
                @click="selectLesson(previousLesson)">
                <i class="icon-arrow-left" /> Aula anterior
            </button>
            <div />
            <button
                v-if="showCourseAction"
                class="show-certificate"
                @click="goToCertificate()">
                Gerar certificado
            </button>
            <button
                v-else-if="nextLesson"
                @click="selectLesson(nextLesson)">
                Próxima aula <i class="icon-arrow-right" />
            </button>
        </div>
        <div
            class="content-container">
            <div
                v-if="lesson.content_id"
                class="content col-10 col-lg-9 col-xl-7">
                <div class="lesson-info">
                    <b>{{ currentModule.title }}</b>
                    <p>{{ currentModule.description }}</p>
                    <p
                        v-if="lesson.tipo_de_conteudo === 'Aula'"
                        class="text-information">
                        {{ lesson.nome_da_aula }}
                    </p>
                    <p
                        v-else-if="lesson.tipo_de_conteudo === 'Quiz'"
                        class="text-information">
                        Quiz {{ lesson.questoes.length }} Questões
                    </p>
                </div>
                <template v-if="isCurrentLessonReleased">
                    <PdfLesson v-if="isPdfLesson" />
                    <WatchLesson v-else-if="isVideoLesson" />
                    <Quiz v-else-if="lesson.tipo_de_conteudo === 'Quiz'" />
                </template>
                <p v-else>
                    Essa aula será disponibilizada dia <b>{{ currentLessonReleaseDate }}</b>. Aguarde!
                </p>
                <div
                    v-if="isPdfLesson"
                    class="lesson-description">
                    <p
                        v-if="hasDescriptionLink"
                        class="text-information">
                        {{ textBeforeLink }} <a
                            :href="textLink"
                            target="_blank">{{ textLink }}</a> {{ textAfterLink }}
                    </p>
                    <p
                        v-else
                        class="text-information">
                        {{ lesson.descricao_da_aula }}
                    </p>
                    <div
                        v-if="supportMaterials.length"
                        class="lesson-support-materials">
                        <b class="support-materials-title">Materiais de apoio</b>
                        <div
                            v-for="(material, idx) in supportMaterials"
                            :key="`pdf-material-${idx}`"
                            class="support-material">
                            <span class="material-name">
                                {{ material.name }}
                            </span>
                            <a
                                :href="material.url"
                                target="_blank"
                                class="btn-download">
                                <i class="icon-download" />
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </b-col>
</template>
<script>
import { mapActions, mapGetters } from "vuex";
import { normalizeSupportMaterials } from "@/core/supportMaterials";
import { parseLessonDescription } from "@/core/utils/lessonDescription";

const WatchLesson = () => import(/* webpackChunkName: "watch-lesson" */ "@/features/watch-lesson");
const Quiz = () => import(/* webpackChunkName: "quiz" */ "@/features/quiz");
const PdfLesson = () => import(/* webpackChunkName: "pdf-lesson" */ "@/features/pdf-lesson");

export default {
  name: "AppContent",
  components: {
    WatchLesson,
    Quiz,
    PdfLesson,
  },
  props: {
    courseId: {
      type: [
        Number, String,
      ],
      required: true,
    },
  },
  computed: {
    ...mapGetters({
      lesson: "getLesson",
      getLessonProgress: "getLessonProgress",
      currentModule: "getCurrentModule",
      previousLesson: "getPreviousLesson",
      nextLesson: "getNextLesson",
      currentLessonRelease: "getCurrentLessonRelease",
      isFinishedCourse: "getIsFinishedCourse",
    }),
    showCourseAction() {
      return this.isFinishedCourse && !this.nextLesson;
    },
    normalizedMaterialType() {
      return this.normalizeMaterialType(this.lesson.tipo_de_material);
    },
    isPdfLesson() {
      if (this.lesson.tipo_de_conteudo !== "Aula") {
        return false;
      }

      if (this.inferPdfByFile(this.lesson)) {
        return true;
      }

      if (this.normalizedMaterialType === "PDF") {
        return true;
      }

      if (this.normalizedMaterialType === "VIDEO") {
        return false;
      }

      return this.inferPdfByFile(this.lesson);
    },
    isVideoLesson() {
      return this.lesson.tipo_de_conteudo === "Aula" && !this.isPdfLesson;
    },
    isCurrentLessonReleased() {
      return !this.currentLessonRelease || this.currentLessonRelease.statusRelease === 1;
    },
    currentLessonReleaseDate() {
      return this.currentLessonRelease ? this.currentLessonRelease.dateRelease : "";
    },
    lessonDescriptionParts() {
      return parseLessonDescription(this.lesson.descricao_da_aula);
    },
    hasDescriptionLink() {
      return this.lessonDescriptionParts.hasLink;
    },
    textBeforeLink() {
      return this.lessonDescriptionParts.beforeLink;
    },
    textLink() {
      return this.lessonDescriptionParts.link;
    },
    textAfterLink() {
      return this.lessonDescriptionParts.afterLink;
    },
    supportMaterials() {
      return normalizeSupportMaterials(this.lesson.materiais_de_apoio);
    },
  },
  methods: {
    ...mapActions([
      "selectLesson",
      "saveLessonProgress",
    ]),
    getCertificateUrl() {
      return `${window.location.href.split("?")[0]}?certificado=show`;
    },
    async goToCertificate() {
      const certificateWindow = window.open("", "_blank");
      const certificateUrl = this.getCertificateUrl();
      const currentProgress = this.getLessonProgress?.progress ?? 0;

      try {
        if (this.lesson?.content_id) {
          await this.saveLessonProgress({
            courseId: this.courseId,
            lessonId: this.lesson.content_id,
            completed: true,
            progress: currentProgress,
          });
        }
      } finally {
        if (certificateWindow) {
          certificateWindow.location = certificateUrl;
        } else {
          window.open(certificateUrl, "_blank");
        }
      }
    },
    normalizeMaterialType(type = "") {
      return String(type)
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim()
        .toUpperCase();
    },
    inferPdfByFile(lesson) {
      const fileCandidates = [
        lesson?.video?.filename,
        lesson?.video?.name,
        lesson?.video?.title,
        lesson?.video?.url,
      ].filter(Boolean);

      const hasPdfFile = fileCandidates.some((value) => /\.pdf($|\?)/i.test(value));
      const hasPdfMime = lesson?.video?.mime_type === "application/pdf" || lesson?.video?.subtype === "pdf";

      return hasPdfFile || hasPdfMime;
    },
  },
};
</script>
<style lang="scss" scoped>
@import "@/assets/scss/variables";

@media (max-width: 766px) {
  .content-container {
    height: unset !important;
    overflow-y: unset !important;
  }
}

.content-container {
  overflow-y: auto;
  height: calc(100vh - 142px);
  .content {
    padding: 48px 0;
    margin: 0 auto;

    .lesson-info {
      margin-bottom: 32px;
      b {
        font-family: "Outfit";
        font-style: normal;
        font-weight: 600;
        font-size: 20px;
        line-height: 100%;
        color: $text-dark;
      }
      p {
        font-family: "Inter";
        font-style: normal;
        font-weight: 400;
        font-size: 14px;
        line-height: 17px;
        color: $text-dark;
        &.text-information {
          color: $grey-03;
        }
      }
    }
    .lesson-description {
      margin-top: 32px;
      p {
        font-family: "Inter";
        font-style: normal;
        font-weight: 400;
        font-size: 16px;
        line-height: 24px;
        color: $text-dark;
      }
    }
    .lesson-support-materials {
      margin-top: 32px;
      .support-materials-title {
        display: block;
        margin-bottom: 16px;
      }
      .support-material {
        background: $white;
        min-height: 38px;
        box-shadow: 2px 4px 8px rgba(0, 18, 63, 0.08);
        border-radius: 4px;
        padding: 10px 16px;
        margin-bottom: 16px;
        display: flex;
        justify-content: space-between;
        align-items: center;
      }
      .material-name {
        font-size: 14px;
        line-height: 17px;
        overflow-wrap: anywhere;
      }
      .btn-download {
        border: none;
        width: 24px;
        height: 24px;
        display: flex;
        background: none;
        justify-content: center;
        align-items: center;
        font-size: 24px;
        color: $text-dark;
        &:hover {
          text-decoration: none;
        }
      }
    }
  }
}

.nav-indicators {
  height: 72px;
  display: flex;
  justify-content: space-between;
  button {
    font-size: 14px;
    line-height: 17px;
    color: $text-dark;
    transition: all 0.2s ease;
    border: none;
    outline: none;
    padding-left: 16px;
    padding-right: 16px;
    background: transparent;

    &.show-certificate {
      background: $primary-dark;
      font-weight: 700;
      color: $white;
      display: flex;
      align-items: center;
      > i {
        color: $white;
        margin-left: 5px;
        font-size: 24px;
      }
      &:hover {
        background: $primary;
      }
    }
    &:hover {
      background: $primary-dark;
      color: $white;
      > i {
        color: $white;
      }
    }

    i {
      color: $primary-dark;
    }
  }
}
</style>
